/**
 * User management mixin
 * Provides user authentication and premium status features
 */
import { markRaw } from 'vue';

export default {
    data() {
        return {

            //! User data - only non-sensitive information
            user_data: {
                email: null,
                display_name: null,
                account_status: 'inactive',
                subscription_status: 'inactive',
                is_premium: false,
                tier: 'free',
                current_period_end: null,
                cancel_at_period_end: false
            },
            
            // AI credits (sum of tier + purchased)
            ai_credits: 0,
            
            // Premium restrictions cache (pr = premium restrictions)
            pr: {},
            pr_last_sync: null,

        };
    },


    computed: {

        max_export_pixel_ratio() {
            return this.user_data.is_premium ? 4 : 2;
        },        

        can_export_at_current_settings() {
            const final_res = this.calculated_final_resolution;
            
            // Check absolute maximum dimensions (10000x10000) - no tier can exceed this
            const max_dimension = Math.max(final_res.width, final_res.height);
            if (max_dimension > 10000) {
                return false;
            }
            
            const max_pixel_ratio = this.user_data.is_premium ? 4 : 1;
            
            // Check pixel ratio limit
            if (this.export_pixel_ratio > max_pixel_ratio) {
                return false;
            }
            
            // Check resolution limit for free users
            if (!this.user_data.is_premium) {
                const max_width = 800;
                const max_height = 1200;
                return final_res.width <= max_width && final_res.height <= max_height;
            }
            
            return true;
        },

        export_limitation_message() {
            const final_res = this.calculated_final_resolution;
            
            // Check absolute maximum dimensions (10000x10000) - applies to all tiers
            const max_dimension = Math.max(final_res.width, final_res.height);
            if (max_dimension > 10000) {
                return this.get_t('premium.dimension_limit_exceeded');
            }
            
            if (this.user_data.is_premium) return '';
            
            const max_width = 800;
            const max_height = 1200;
            
            if (this.export_pixel_ratio > 1) {
                return this.get_t('export.free_limit_exceeded');
            }
            
            if (final_res.width > max_width || final_res.height > max_height) {
                return this.get_t('export.resolution_too_high_message');
            }
            
            return '';
        },

        export_button_text() {
            if (!this.can_export_at_current_settings) {
                const final_res = this.calculated_final_resolution;
                const max_dimension = Math.max(final_res.width, final_res.height);
                
                // If exceeding absolute maximum dimensions, show limit message
                if (max_dimension > 10000) {
                    return this.get_t('export.dimension_limit_reached');
                }
                
                // Otherwise show upgrade required message
                return this.get_t('export.upgrade_required');
            }
            
            return this.get_t('export.download');
        },

    },
    
    mounted() {
        // Sync premium restrictions on startup
        this.sync_premium_restrictions();        
        // Set up periodic sync every 15 seconds
        setInterval(() => {
            this.sync_premium_restrictions();
        }, 15000);
    },

    methods: {

        //! User
        async check_user_status(frontend_language = 'en') {
            // Check for existing PHP session on app load
            return await this.check_php_session(frontend_language);
        },

        async check_premium_status() {
            const form_data = new FormData();
            form_data.append('request', "check_premium_status");
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });

                //console.log("Premium status response:", response.data);
                
                // Update user data with server data (protected)
                this.user_data = markRaw(Object.freeze({
                    ...this.user_data,
                    is_premium: response.data.is_premium || false,
                    tier: response.data.tier || 'free',
                    subscription_status: response.data.subscription_status || 'inactive',
                    cancel_at_period_end: response.data.cancel_at_period_end || false,
                    has_scheduled_downgrade: response.data.has_scheduled_downgrade || false,
                    downgrade_tier: response.data.downgrade_tier || null,
                    downgrade_date: response.data.downgrade_date || null,
                    stripe_schedule_id: response.data.stripe_schedule_id || null
                }));
                return this.user_data.is_premium;
            } 
            catch (error) {
                // console.error("Error checking premium status:", error);
                // Default to false for security (protected)
                this.user_data = markRaw(Object.freeze({
                    ...this.user_data,
                    is_premium: false,
                    tier: 'free',
                    subscription_status: 'inactive'
                }));
                return false;
            }
        },

        async validate_premium_action(action_type, action_data) {
            const form_data = new FormData();
            form_data.append('request', 'validate_premium_action');
            form_data.append('action_type', action_type);
            form_data.append('action_data', JSON.stringify(action_data));
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.is_allowed !== undefined) {
                    return {
                        allowed: response.data.is_allowed,
                        message: response.data.message || '',
                        suggested_action: response.data.suggested_action || null
                    };
                } else {
                    return {
                        allowed: false,
                        message: response.data.message || 'Validation failed',
                        suggested_action: null
                    };
                }
            } catch (error) {
                // console.error('Error validating premium action:', error);
                return {
                    allowed: false,
                    message: 'Connection error. Please try again.',
                    suggested_action: null
                };
            }
        },

        async check_premium_limit(action_type, action_data = {}) {
            const validation = await this.validate_premium_action(action_type, action_data);
            
            if (!validation.allowed) {
                // Use server-provided message and action type
                const message = validation.message && validation.message.trim() 
                    ? validation.message 
                    : this.get_t(`premium.${action_type}_limit`);
                
                // Use server-suggested action type if provided, otherwise use original action_type
                const suggested_action = validation.suggested_action || action_type;
                
                this.show_premium_modal(suggested_action, message);
                return false;
            }
            
            return true;
        },

        async sync_premium_restrictions() {
            const form_data = new FormData();
            form_data.append('request', 'check_premium_status');
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                // Check for session expiry
                if (response.data.status === 'session_expired') {
                    this.handle_session_expired_other_device(response.data.message);
                    return false;
                }
                
                if (response.data.limits) {
                    // Update restrictions cache with server data
                    this.pr = markRaw(Object.freeze({...response.data.limits}));
                    this.pr_last_sync = Date.now();
                    
                    // Update user data with server data (protected)
                    this.user_data = markRaw(Object.freeze({
                        ...this.user_data,
                        is_premium: response.data.is_premium || false,
                        tier: response.data.tier || 'free',
                        subscription_status: response.data.subscription_status || 'inactive',
                        cancel_at_period_end: response.data.cancel_at_period_end || false,
                        has_scheduled_downgrade: response.data.has_scheduled_downgrade || false,
                        downgrade_tier: response.data.downgrade_tier || null,
                        downgrade_date: response.data.downgrade_date || null,
                        stripe_schedule_id: response.data.stripe_schedule_id || null
                    }));
                    
                    //console.log('Premium restrictions synced:', this.pr);
                    return true;
                }
            } catch (error) {
                // console.error('Error syncing premium restrictions:', error);
            }
            
            return false;
        },

        check_local_restriction(restriction_type, current_value = 0) {
            // If no restrictions cached, allow action (fail open)
            if (!this.pr || Object.keys(this.pr).length === 0) {
                return true;
            }
            
            const limit = this.pr[restriction_type];
            if (limit === undefined) {
                return true; // Unknown restriction, allow
            }
            
            // Handle boolean restrictions (like copy_paste_styles)
            if (typeof limit === 'boolean') {
                if (!limit) {
                    const message_key = `premium.${restriction_type}_limit`;
                    const message = this.get_t(message_key);
                    this.show_premium_modal(restriction_type, message);
                    return false;
                }
                return true;
            }
            
            // Handle numeric restrictions
            if (current_value >= limit) {
                // Show premium modal directly
                const message_key = `premium.${restriction_type}_limit`;
                const message = this.get_t(message_key);
                this.show_premium_modal(restriction_type, message);
                return false;
            }
            
            return true;
        },

        validate_premium_dropdown_option(restriction_type, selected_value) {
            // Special validation for dropdown options that have premium requirements
            let is_premium_option = false;
            let allowed_value = selected_value;
            
            if (restriction_type === 'min_autosave_interval') {
                // Lower values (faster autosave) are premium
                is_premium_option = parseInt(selected_value) < 20; // Anything less than 20 minutes is premium
                allowed_value = is_premium_option ? 20 : selected_value;
            } else if (restriction_type === 'max_undo_steps') {
                // Higher values are premium
                is_premium_option = parseInt(selected_value) > 20; // More than 20 steps is premium
                allowed_value = is_premium_option ? 20 : selected_value;
            }
            
            // If it's a premium option and user is not premium, block it
            if (is_premium_option && !this.user_data.is_premium) {
                // Reset to allowed value
                this.$nextTick(() => {
                    if (restriction_type === 'min_autosave_interval') {
                        this.preferences.editor.autosave_interval = allowed_value;
                    } else if (restriction_type === 'max_undo_steps') {
                        this.preferences.editor.history_steps = allowed_value;
                    }
                });
                
                // Show premium modal
                const message_key = `premium.${restriction_type}_limit`;
                const message = this.get_t(message_key);
                this.show_premium_modal(restriction_type, message);
                return false;
            }
            
            return true;
        },



        // Check PHP session for auto login
        async check_php_session(frontend_language = 'en') {
            const form_data = new FormData();
            form_data.append('request', 'check_php_session');
            form_data.append('language', frontend_language);
            
            try {
                const response = await axios.post('ajax.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                if (response.data.status === 'success' && response.data.user) {
                    this.user_data = markRaw(Object.freeze({
                        email: response.data.user.email,
                        display_name: response.data.user.display_name,
                        is_premium: response.data.user.is_premium,
                        account_status: 'active',
                        tier: response.data.user.tier
                    }));
                    
                    // Set AI credits from total
                    this.ai_credits = response.data.user.ai_credits_total || 0;
                    
                    // Load user preferences from session response
                    if (typeof this.load_preferences_from_response === 'function') {
                        this.load_preferences_from_response(response.data.preferences);
                    }
                    
                    // Mikogen: Save authentication status to localStorage
                    if (!this.comic_mode) {
                        localStorage.setItem('mikogen_auth_status', 'true');
                    }

                    console.log('Auto-logged in from PHP session:', this.user_data.email);
                } else if (response.data.status === 'session_expired') {
                    // Session expired due to login from another device
                    this.handle_session_expired_other_device(response.data.message);
                } else {
                    // No session - initialize default preferences for guest users
                    await this.initialize_default_preferences();
                }
                
                return response.data;
            } catch (error) {
                console.error('Error checking PHP session:', error);
                // On error, initialize default preferences for guest users
                await this.initialize_default_preferences();
                return null;
            }
        },
        
        async initialize_default_preferences() {
            // Just ensure preferences are initialized and apply them
            if (typeof this.apply_preferences === 'function') {
                this.apply_preferences();
            }
        },
        
        handle_session_expired_other_device(message) {
            // Reset user data to guest state
            this.user_data = markRaw(Object.freeze({
                email: null,
                display_name: null,
                account_status: 'inactive',
                subscription_status: 'inactive',
                is_premium: false
            }));
            
            // Show notification about session expiry
            this.show_notification_modal(message || this.get_t('auth.session_expired_other_device'), 'warning');
            
            // Initialize default preferences for guest user
            this.initialize_default_preferences();
        },


        gtag_report_conversion(url) {
            var callback = function () {
                if (typeof(url) != 'undefined') {
                window.location = url;
                }
            };
            gtag('event', 'conversion', {
                'send_to': 'AW-17424333724/veqGCNSZxv0aEJz3yPRA',
                'value': 1.0,
                'currency': 'EUR',
                'event_callback': callback
            });
            return false;
        }



    }
};